import React, { useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from 'react-leaflet';
import L from 'leaflet';
import type { MapViewProps } from './types';

// Исправление иконок Leaflet
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Синяя иконка для текущего пользователя
const UserIcon = L.icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="35" viewBox="0 0 25 41">
      <path fill="red" stroke="#1565C0" stroke-width="1" d="M12.5 0C5.6 0 0 5.6 0 12.5c0 8.4 12.5 28.5 12.5 28.5S25 20.9 25 12.5C25 5.6 19.4 0 12.5 0z"/>
      <circle fill="white" cx="12.5" cy="12.5" r="6"/>
    </svg>
  `),
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

/**
 * Контроллер для управления картой (центрирование при клике)
 */
interface MapControllerProps {
  center: [number, number] | null;
  zoom?: number;
}

const MapController: React.FC<MapControllerProps> = ({ center, zoom = 15 }) => {
  const map = (useMap as any)();
  
  useEffect(() => {
    if (center && map) {
      map.setView(center, zoom, { animate: true });
    }
  }, [center, zoom, map]);
  
  return null;
};

/**
 * Компонент интерактивной карты с маркерами аптек
 */
export const MapView: React.FC<MapViewProps> = ({
  locations,
  mapCenter,
  selectedLocation,
  userLocation,
  radiusFilter,
  markersRef
}) => {
  const currentUsername = localStorage.getItem('username');
  
  return (
    <Box 
      sx={{ 
        width: '100%', 
        height: '500px', 
        overflow: 'hidden',
        borderRadius: 2,
        border: '1px solid #e0e0e0'
      }}
    >
      <MapContainer
        center={mapCenter}
        zoom={12}
        style={{ height: '100%', width: '100%' }}
      >
        <MapController center={selectedLocation} zoom={16} />
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        {/* Маркеры аптек */}
        {locations.map((location, index) => {
          const isCurrentUser = !!(currentUsername && location.username?.toLowerCase() === currentUsername.toLowerCase());
          
          return (
            <Marker 
              key={index} 
              position={[location.latitude, location.longitude]}
              icon={isCurrentUser ? UserIcon : DefaultIcon}
              zIndexOffset={isCurrentUser ? 1000 : 0}
              ref={(ref) => {
                if (ref) {
                  markersRef.current.set(location.username, ref as any);
                }
              }}
            >
              <Popup>
                <Box sx={{ minWidth: 200 }}>
                  <Typography variant="subtitle1" fontWeight="bold" color={isCurrentUser ? 'primary' : 'inherit'}>
                    {location.username} {isCurrentUser && '(ВЫ)'}
                  </Typography>
                  {location.address && (
                    <Typography variant="body2" sx={{ my: 1 }}>
                      📍 {location.address}
                    </Typography>
                  )}
                  <Typography variant="caption" display="block" color="text.secondary">
                    Координаты: {location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}
                  </Typography>
                </Box>
              </Popup>
            </Marker>
          );
        })}

        {/* Маркер текущего пользователя - если он не в списке (отфильтрован) */}
        {userLocation && !locations.find(loc => loc.username?.toLowerCase() === currentUsername?.toLowerCase()) && (
          <Marker 
            position={[userLocation.latitude, userLocation.longitude]}
            icon={UserIcon}
            zIndexOffset={1000}
            ref={(ref) => {
              if (ref) {
                markersRef.current.set(userLocation.username, ref as any);
              }
            }}
          >
            <Popup>
              <Box sx={{ minWidth: 200 }}>
                <Typography variant="subtitle1" fontWeight="bold" color="primary">
                  {userLocation.username} (ВЫ)
                </Typography>
                {userLocation.address && (
                  <Typography variant="body2" sx={{ my: 1 }}>
                    📍 {userLocation.address}
                  </Typography>
                )}
                <Typography variant="caption" display="block" color="text.secondary">
                  Координаты: {userLocation.latitude.toFixed(6)}, {userLocation.longitude.toFixed(6)}
                </Typography>
              </Box>
            </Popup>
          </Marker>
        )}

        {/* Круг радиуса поиска */}
        {radiusFilter > 0 && userLocation && (
          <Circle
            center={[userLocation.latitude, userLocation.longitude]}
            radius={radiusFilter * 1000}
            pathOptions={{
              color: 'blue',
              fillColor: 'blue',
              fillOpacity: 0.1
            }}
          />
        )}
      </MapContainer>
    </Box>
  );
};

export default MapView;

